import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { TaglineMarquee } from "@/components/Extras";

const CHAPTERS = [
  { n: "01", title: "Direct Buyer", body: "We purchase non-prime, off-grade, wide-spec, transitional and purge resin — LDPE, LLDPE, HDPE, PP, PET and engineering polymers — directly from producers, molders and converters. Principal buyer on our own account, no broker chain." },
  { n: "02", title: "Recovery Partner", body: "Scheduled post-industrial recovery programs for edge trim, purge, rejects, obsolete inventory, packaging and PPE. Structured, documented and built around your production rhythm." },
  { n: "03", title: "Logistics Coordinator", body: "FTL, FCL and mixed-load pickup coordinated across Canada and the United States. One team handles scheduling, paperwork, weigh tickets and BOLs — from first call through recurring pickups." },
  { n: "04", title: "Secondary-Material Marketer", body: "Placement into verified downstream processors and compounders across international markets — moving your material into productive reuse instead of landfill." },
  { n: "05", title: "Long-Term Contract Buyer", body: "Recurring monthly volume contracts, not spot deals — pricing stability, dependable offtake and predictable cash flow for finance and procurement teams." },
];

const About = () => (
  <div data-testid="about-page">
    <PageHero eyebrow="/ ABOUT SAYE IMPEX" title={["Manufacturer recovery.", "Secondary-material buyer."]}
      subtitle="Direct buyer of non-prime and off-grade resin. Stock lot and post-industrial recyclable plastic recovery partner. North American pickup coordinator. International secondary-material marketer. Long-term recurring-contract buyer." />

    <section className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
        <FadeUp>
          <img src="/images/about-plant.png" alt="SAYE facility"
            onError={(e) => { e.currentTarget.src = "/images/hero-facility.png"; }}
            className="w-full aspect-[4/3] object-cover" data-testid="about-image" />
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="text-navy/80 text-2xl md:text-3xl font-display font-bold tracking-tight leading-snug">
            A principal buyer of post-industrial polymer, paper and PPE streams — not a broker, not a middleman.
          </p>
          <p className="text-navy/60 mt-6 leading-relaxed">
            SAYE IMPEX RECYCLING INC. contracts directly with manufacturers, converters, molders and
            brand owners across Canada and the United States. We purchase on recurring monthly
            volume, coordinate FTL and FCL pickup, and place graded material into verified
            international downstream processors — one team, one point of contact, end-to-end.
          </p>
        </FadeUp>
      </div>
    </section>

    <TaglineMarquee />

    {/* Manifesto chapters */}
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      {CHAPTERS.map((c) => (
        <FadeUp key={c.n}>
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 py-14 md:py-20 border-t border-navy/10" data-testid={`chapter-${c.n}`}>
            <div className="md:col-span-2 font-mono text-2xl text-emerald">{c.n}</div>
            <h3 className="md:col-span-4 font-display font-black text-3xl md:text-4xl tracking-tighter text-navy leading-none">{c.title}</h3>
            <p className="md:col-span-6 text-navy/70 text-lg leading-relaxed">{c.body}</p>
          </div>
        </FadeUp>
      ))}
    </section>

    <section className="bg-navy py-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <h3 className="font-display font-black text-white text-3xl md:text-4xl tracking-tighter">Let's build a partnership.</h3>
        <Link to="/contact" data-testid="about-cta"
          className="group bg-emerald text-navy font-mono text-xs uppercase tracking-widest px-7 py-4 flex items-center gap-2 hover:gap-4 transition-all">
          Contact Us <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  </div>
);

export default About;
